import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import cc, { CurrencyCodeRecord } from 'currency-codes';

import SplitPaymentBar from '../../components/SplitPaymentBar';
import SplitPaymentDot from '../../components/SplitPaymentDot';
import SplitPaymentIcon from '../../../components/Icons/split_payment';
import ChevronIcon from '../../../components/Icons/chevron';
import { Money, HumanPercentage } from '../../../components/Formatters';
import Tooltip from '../../../components/Tooltip';
import { colors } from '../../../styles/constants';
import { Button, ButtonGroup } from '../../../components/Buttons';

import { PayoutSplit, PayoutFeeSplit } from '../../../types/checkout';
import { FormattedInput, Input, Checkbox } from '../../../components/Forms';
import { getLocaleNumeralDelimiter, getLocaleNumeralDecimal } from '../../../helpers/intl';
import { fromMonetaryAmountString, toMonetaryAmountString } from '../../../helpers/formatters';
import { Formik } from 'formik';

import {
    filterEmptySplits,
    filterEmptySplitsInFeeSplit,
    replaceSplitAt,
    checkFeeSplitValid,
    checkPayoutSplitsValid
} from '../../splitPaymentHelpers';

type SplitPaymentRowProps = {
    payoutSplits: PayoutSplit[];
    feeSplit: PayoutFeeSplit;
    currency: string;
    amount: number;
    changeSplits: (splits: PayoutSplit[], feeSplit: PayoutFeeSplit) => void;
    open: boolean;
    toggleSplit: () => void;
};

const RefundSplitPaymentRow = ({ payoutSplits, feeSplit, currency, amount, changeSplits, open, toggleSplit }: SplitPaymentRowProps) => {
    const { t, i18n } = useTranslation();
    const total = payoutSplits.reduce((sum, p) => sum + p.amount, 0);
    const hasSplits = payoutSplits.length > 0;
    const exponent = cc.code(currency)
        ? (cc.code(currency) as CurrencyCodeRecord).digits
        : 2;
    const lng = i18n.language;
    const delimiter = getLocaleNumeralDelimiter(lng);
    const numeralDecimalMark = getLocaleNumeralDecimal(lng);
    const isValid = total === amount;

    return <Formik
        key={amount}
        enableReinitialize
        initialValues={{
            payoutSplits,
            feeSplitToggles: payoutSplits.map(p => (feeSplit.destinations ?? []).includes(p.payout_destination_id)),
        }}
        validate={values => {
            if (!checkPayoutSplitsValid(amount, values.payoutSplits)) {
                return { payoutSplits: 'invalid' };
            }
            if (!checkFeeSplitValid(values.payoutSplits, values.feeSplitToggles)) {
                return { feeSplitToggles: 'invalid' };
            }
        }}
        onSubmit={(values) => {
            const nextSplits = filterEmptySplits(values.payoutSplits);
            const nextFeeSplit = filterEmptySplitsInFeeSplit(values.payoutSplits, values.feeSplitToggles);
            changeSplits(nextSplits, nextFeeSplit);
        }}
    >{({ values, errors, setFieldValue, resetForm, submitForm, dirty, submitCount, setValues }) => {
            const formTotal = values.payoutSplits.reduce((sum, p) => sum + p.amount, 0);
            const formMissing = amount - formTotal;

            return <><SplitBarWrapper onClick={() => {
                if (!open || !dirty) {
                    toggleSplit();
                    resetForm();
                }
            }}>
                <Tooltip title={<>{t('payments.fields.split_payment')}</>}><SplitPaymentIconWrapper className={hasSplits ? '' : 'disabled'}><SplitPaymentIcon /></SplitPaymentIconWrapper></Tooltip>
                <Spacer />
                <SplitPaymentBar payoutSplits={values.payoutSplits} currency={currency} missing={formMissing > 0 ? formMissing: 0} />
                <Spacer />
                <ChevronWrapper open={open && hasSplits} disabled={!hasSplits} type="button">
                    <ChevronIcon />
                </ChevronWrapper>
            </SplitBarWrapper>
            <Collapse in={(open && hasSplits) || !isValid}>
                <SplitFormGrid>
                    {values.payoutSplits.map((p, i) => <React.Fragment key={`split-destination-${i}`} >
                        <SplitPaymentDot key={i} index={i} />
                        <InputWrapper>
                            <FormattedInput
                                label={t('payments.fields.split') + ` ${i + 1}`}
                                options={{
                                    numeral: true,
                                    numericOnly: true,
                                    numeralThousandsGroupStyle: 'thousand',
                                    delimiter,
                                    numeralIntegerScale: `${amount}`.length,
                                    numeralDecimalMark,
                                    numeralPositiveOnly: true,
                                    numeralDecimalScale: exponent,
                                }}
                                autoComplete="off"
                                placeholder="0"
                                type="tel"
                                inputMode="numeric"
                                name={`refund-split-amount-${i}`}
                                value={toMonetaryAmountString(lng, p.amount, currency)}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    const amount = fromMonetaryAmountString(lng, e.currentTarget.value, currency);
                                    const newSplit = { ...p, amount };
                                    const nextSplits = replaceSplitAt(values.payoutSplits, newSplit, i);
                                    setFieldValue('payoutSplits', nextSplits);
                                }}
                                onBlur={(e: React.FormEvent<HTMLInputElement>) => {
                                    const amount = fromMonetaryAmountString(lng, e.currentTarget.value, currency);
                                    const newSplit = { ...p, amount };
                                    const nextSplits = replaceSplitAt(values.payoutSplits, newSplit, i);
                                    setFieldValue('payoutSplits', nextSplits);
                                }}
                            />
                        </InputWrapper>
                        <PercentageWrapper>
                            <HumanPercentage value={(p.amount / amount) * 100} />
                        </PercentageWrapper>
                        <InputWrapper>
                            <Input
                                placeholder=""
                                label={t('payments.fields.payout_destination')}
                                autoComplete="off"
                                name={`refund-split-payout-destination-${i}`}
                                value={p.payout_destination_id}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    const newSplit = { ...p, payout_destination_id: e.currentTarget.value };
                                    const nextSplits = replaceSplitAt(values.payoutSplits, newSplit, i);
                                    setFieldValue('payoutSplits', nextSplits);
                                }}
                                onBlur={(e: React.FormEvent<HTMLInputElement>) => {
                                    const newSplit = { ...p, payout_destination_id: e.currentTarget.value };
                                    const nextSplits = replaceSplitAt(values.payoutSplits, newSplit, i);
                                    setFieldValue('payoutSplits', nextSplits);
                                }}
                            />
                        </InputWrapper>
                        <CheckboxWrapper>
                            <Checkbox
                                label={t('payments.refund.form.fee_split_label')}
                                name={`refund-fee-split-${i}`}
                                checked={values.feeSplitToggles[i] || false}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    setFieldValue(`feeSplitToggles`, values.feeSplitToggles.map((t, j) => j === i ? e.currentTarget.checked : t));
                                }}
                                topLabel
                            />
                        </CheckboxWrapper>
                    </React.Fragment>)}
                    <SpanAllColumns>
                        <AddSplitButton
                            type="button"
                            onClick={() => {
                                setValues({
                                    payoutSplits: [...values.payoutSplits, { amount: 0, payout_destination_id: '' }],
                                    feeSplitToggles: [...values.feeSplitToggles, true],
                                });
                            }}
                        >
                            <AddSplitPaymentDot />
                            {t('payments.refund.form.add_split')}
                        </AddSplitButton>
                    </SpanAllColumns>
                    <SpanAllColumns>
                        <Hr />
                    </SpanAllColumns>
                    <SpanTwoColumns>
                        <TotalWrapper>
                            {t('payments.refund.form.item_split_total')}
                            <ToatalMoneyWrapper><Money amount={formTotal} currency={currency} hideCurrency /></ToatalMoneyWrapper>
                        </TotalWrapper>
                    </SpanTwoColumns>
                    <PercentageWrapper className={(submitCount > 0 && errors.payoutSplits) ? 'error' : ''}><HumanPercentage value={formTotal/amount * 100} /></PercentageWrapper>
                    <span>
                        {(submitCount > 0 && errors.payoutSplits) ? <Error>{t('payments.refund.form.validation.payout_split_amount')}</Error> : ' '}
                        {(submitCount > 0 && errors.feeSplitToggles) ? <Error>{t('payments.refund.form.validation.payout_split_fee')}</Error> : ' '}
                    </span>
                    <SpanAllColumns>
                        <Hr />
                    </SpanAllColumns>

                    <SpanAllColumns>
                        <MarginTopButtonGroup>
                            <Button type="button" onClick={() => submitForm()}>
                                {t('payments.refund.form.save_split')}
                            </Button>
                            <Button type="button" className="alt" onClick={() => {resetForm();toggleSplit();}}>
                                {t('payments.refund.form.cancel_split')}
                            </Button>
                        </MarginTopButtonGroup>
                    </SpanAllColumns>
                </SplitFormGrid>
                <Hr/>
            </Collapse></>;
        }}</Formik>;
};


const SplitBarWrapper = styled.div`
    cursor: pointer;
    width: 100%;
    display:flex;
    justify-content: space-between;
    align-items: center;
`;

const SplitPaymentIconWrapper = styled.span`
    &.disabled{
        opacity: 0.1
    }
`;

type OpenProps = {
    open: boolean;
}

const ChevronWrapper = styled.button<OpenProps>`
    border: none;
    background: transparent;
    color: current-color;
    padding: 0;
    margin: 0;
    transition: transform 150ms ease-out;
    cursor: pointer;

    ${props => props.open ? `
        transform: scaleY(-1);
    ` : ''}

    &:focus {
        outline: none;
    }

    :disabled{
        opacity: 0.1
    }
`;
const Spacer = styled.div`
    width: 16px;
`;

const SplitFormGrid = styled.div`
    margin-left: 44px;
    margin-right: 44px;
    display: grid;
    grid-template-columns: 40px 1fr 20% 1fr 8%;
    align-items: center;
`;

const Hr = styled.hr`
    border: none;
    border-top: thin solid #e6e6e6;
`;

const SpanAllColumns = styled.div`
    grid-column: 1 / -1;
`;
const SpanTwoColumns = styled.div`
    grid-column: span 2; 
`;

const ToatalMoneyWrapper = styled.div`
    text-align: right;
    margin-right: 16px;
`;

const PercentageWrapper = styled.div`
    font-weight: 700;
    text-align: center;
    margin-left: 24px;
    margin-right: 24px;
    white-space: nowrap;

    &.error{
        color: ${colors.invalid};
    }
`;

const CheckboxWrapper = styled.div`
    min-width: 40px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
`;

const Error = styled.div`
    color: ${colors.invalid};
    font-weight: 400;
`;

const InputWrapper = styled.div`
    width: 100%;
`;

const TotalWrapper = styled.div`
    display:flex;
    justify-content: space-between;
    font-weight: 700;
    text-transform: uppercase;
    align-items: center;

    padding-top: 8px;
    padding-bottom: 8px;
`;

const AddSplitButton = styled.button`
    width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background-color: transparent;
    color: ${colors.text};
    text-align: left;
    display: flex;
    align-items: center;
    margin-top: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    &:hover {
        background-color: transparent;
    }
    &:active {
        background-color: transparent;
    }
    &:focus {
        outline: none;
    }
`;

const MarginTopButtonGroup = styled(ButtonGroup)`
    margin-top: 16px;
`;

const AddSplitPaymentDot = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 12px;
 
    border: 1px dashed ${colors.text};
    position: relative;
    margin-right: 16px;


    &:after{
        content: '';
        position: absolute;
        top: 4px;
        left: 10px;
        bottom: 4px;
        width: 2px;
        background: ${colors.text};
    }
    &:before{
        content: '';
        position: absolute;
        top: 10px;
        left: 4px;
        right: 4px;
        height: 2px;
        background: ${colors.text};
    }
`;


export default RefundSplitPaymentRow;

